import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../../context/I18nContext';
import { Card, CardContent } from '../../components/ui/Card';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiDollarSign, FiShoppingBag, FiUsers, FiBox, FiArrowUpRight, FiRefreshCw } = FiIcons;

const StatCard = ({ title, value, icon, trend, colorClass = "bg-zinc-50" }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-zinc-500">{title}</h3>
        <div className={`w-10 h-10 ${colorClass} rounded-xl flex items-center justify-center`}>
          <SafeIcon icon={icon} className="w-5 h-5 text-zinc-700" />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-zinc-900">{value}</span>
        {trend && (
          <span className="text-xs font-semibold text-emerald-600 flex items-center gap-0.5">
            <SafeIcon icon={FiArrowUpRight} className="w-3 h-3" />
            {trend}
          </span>
        )}
      </div>
    </CardContent>
  </Card>
);

export const DashboardOverview = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ revenue: 0, orders: 0, products: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: supplier } = await supabase
      .from('suppliers_1739200000000')
      .select('id')
      .eq('owner_id', user.id)
      .single();

    if (supplier) {
      const [productsRes, ordersRes] = await Promise.all([
        supabase.from('products_1739200000000').select('id', { count: 'exact' }).eq('supplier_id', supplier.id),
        supabase.from('order_items_1739200000000').select('total_price').eq('supplier_id', supplier.id)
      ]);

      const rev = ordersRes.data?.reduce((sum, item) => sum + item.total_price, 0) || 0;
      setStats({
        revenue: rev,
        orders: ordersRes.data?.length || 0,
        products: productsRes.count || 0
      });
    }
    setLoading(false);
  };

  if (loading) return (
    <div className="h-64 flex items-center justify-center">
      <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-zinc-300" />
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('dashboard.title')}</h1>
          <p className="text-zinc-500 text-sm">{t('dashboard.welcome')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard title={t('dashboard.revenue')} value={`€${stats.revenue.toFixed(2)}`} icon={FiDollarSign} colorClass="bg-amber-50" />
        <StatCard title={t('dashboard.orders')} value={stats.orders} icon={FiShoppingBag} colorClass="bg-blue-50" />
        <StatCard title={t('dashboard.products')} value={stats.products} icon={FiBox} colorClass="bg-emerald-50" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="p-8">
          <h3 className="font-bold text-zinc-900 mb-6 flex items-center gap-2">
            <SafeIcon icon={FiArrowUpRight} className="text-[#D4B483]" /> {t('dashboard.insights')}
          </h3>
          <div className="space-y-6">
            <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">{t('dashboard.topCategory')}</p>
              <p className="text-sm font-bold text-zinc-900">Biologische Producten</p>
            </div>
            <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">{t('dashboard.avgOrders')}</p>
              <p className="text-sm font-bold text-zinc-900">0.0 ({t('dashboard.launchPhase')})</p>
            </div>
          </div>
        </Card>

        <Card className="p-8 flex items-center justify-center bg-zinc-900 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4B483]/10 rounded-full blur-3xl" />
          <div className="text-center relative z-10">
            <h3 className="text-xl font-bold mb-2">{t('dashboard.helpTitle')}</h3>
            <p className="text-zinc-400 text-sm mb-6">{t('dashboard.helpDesc')}</p>
            <button className="px-6 py-2 bg-[#D4B483] text-[#1A2E22] rounded-full text-xs font-bold hover:bg-[#c4a473] transition-colors">
              {t('dashboard.contactSupport')}
            </button>
          </div>
        </Card>
      </div>
    </motion.div>
  );
};